import { useGetRecordRepresentation, useLocaleState } from "ra-core";
import { ReferenceField } from "@/components/admin/reference-field";
import { cn } from "@/lib/utils";

import { CompanyAvatar } from "../companies/CompanyAvatar";
import { Avatar } from "../contacts/Avatar";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Contact, Task } from "../types";
import { isTaskOverdue } from "./taskStatuses";

// "25 août 2024, 13:07": no seconds, the month in words.
const DUE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: "medium",
  timeStyle: "short",
};

/** Label of the task type, nothing for "none". */
export const TaskTypeLabel = ({ task }: { task: Task }) => {
  const { taskTypes } = useConfigurationContext();
  if (!task.type || task.type === "none") return null;
  return (
    <>
      {taskTypes.find((type) => type.value === task.type)?.label ?? task.type}
    </>
  );
};

/** Due date, in red once an open task is overdue. */
export const TaskDueDate = ({
  task,
  className,
}: {
  task: Task;
  className?: string;
}) => {
  const [locale = "fr"] = useLocaleState();
  return (
    <span
      className={cn(
        "tabular-nums",
        isTaskOverdue(task) && "text-destructive font-medium",
        className,
      )}
    >
      {new Intl.DateTimeFormat(locale, DUE_DATE_FORMAT).format(
        new Date(task.due_date),
      )}
    </span>
  );
};

/** The company and/or contact the task is about, with their avatar. */
export const TaskTarget = ({
  task,
  withAvatar = true,
}: {
  task: Task;
  withAvatar?: boolean;
}) => {
  const getContactRepresentation = useGetRecordRepresentation("contacts");
  return (
    <span className="flex flex-col min-w-0">
      {task.company_id != null && (
        <ReferenceField<Task, Company>
          source="company_id"
          reference="companies"
          record={task}
          link="show"
          render={({ referenceRecord }) =>
            referenceRecord ? (
              <span className="flex items-center gap-2 min-w-0">
                {withAvatar && (
                  <CompanyAvatar
                    record={referenceRecord as Company}
                    width={20}
                    height={20}
                  />
                )}
                <span className="truncate">{referenceRecord.name}</span>
              </span>
            ) : null
          }
        />
      )}
      {task.contact_id != null && (
        <ReferenceField<Task, Contact>
          source="contact_id"
          reference="contacts"
          record={task}
          link="show"
          render={({ referenceRecord }) =>
            referenceRecord ? (
              <span
                className={cn(
                  "flex items-center gap-2 min-w-0",
                  task.company_id != null && "text-muted-foreground",
                )}
              >
                {withAvatar && task.company_id == null && (
                  <Avatar
                    record={referenceRecord as Contact}
                    width={20}
                    height={20}
                  />
                )}
                <span className="truncate">
                  {getContactRepresentation(referenceRecord)}
                </span>
              </span>
            ) : null
          }
        />
      )}
    </span>
  );
};
